import { useEffect, useMemo, useState } from "react";
import { RefreshCcw, Users } from "lucide-react";
import api from "../services/api";
import ViewerSidebar from "../components/ViewerSidebar";
import { downloadEsmpFile } from "../utils/fileDownload";

const IN_PROGRESS_STATUSES = ["Submitted", "Pending", "Overdue"];
const COMPLETED_STATUSES = ["Approved", "Returned", "Rejected"];

const parseEsmpListPayload = (res) => {
  const payload = res?.data?.data;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.esmps)) return payload.esmps;
  return [];
};

const StatCard = ({ label, value, accentClass }) => {
  return (
    <div className={`bg-white p-4 rounded-xl shadow border-l-4 ${accentClass}`}>
      <p className="text-xs text-gray-500">{label}</p>
      <h3 className="text-2xl font-bold">{value}</h3>
    </div>
  );
};

export default function ViewerReviewerWorkload() {
  const [esmps, setEsmps] = useState([]);
  const [reviewers, setReviewers] = useState([]);
  const [selectedReviewerId, setSelectedReviewerId] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [esmpRes, reviewersRes] = await Promise.all([
        api.get("/esmp/list"),
        api.get("/esmp/reviewers"),
      ]);

      const esmpList = parseEsmpListPayload(esmpRes);
      const reviewerList = reviewersRes?.data?.data ?? [];

      setEsmps(Array.isArray(esmpList) ? esmpList : []);
      setReviewers(Array.isArray(reviewerList) ? reviewerList : []);
    } catch (e) {
      console.error("Failed to load reviewer workload:", e);
      const status = e?.response?.status;
      if (status === 403) {
        setError(
          "Your role is not permitted to view reviewer workload details. Please contact your administrator."
        );
      } else {
        setError(e?.response?.data?.message || "Failed to load reviewer workload");
      }
      setEsmps([]);
      setReviewers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const reviewerNameById = useMemo(() => {
    const map = new Map();
    for (const r of reviewers) {
      if (r?.id != null && r?.name) map.set(String(r.id), r.name);
    }
    return map;
  }, [reviewers]);

  const reviewerBuckets = useMemo(() => {
    const assigned = new Map();
    const unassigned = [];

    for (const esmp of esmps) {
      const reviewerId = esmp?.reviewer_id;
      if (!reviewerId) {
        unassigned.push(esmp);
        continue;
      }

      const key = String(reviewerId);
      const bucket = assigned.get(key) || [];
      bucket.push(esmp);
      assigned.set(key, bucket);
    }

    return { assigned, unassigned };
  }, [esmps]);

  const workloadRows = useMemo(() => {
    const rows = [];

    for (const [reviewerId, bucket] of reviewerBuckets.assigned.entries()) {
      const reviewerName = reviewerNameById.get(reviewerId);
      if (!reviewerName) continue; // enforce "name not id" requirement

      const inProgress = bucket.filter((e) => IN_PROGRESS_STATUSES.includes(e?.status)).length;
      const completed = bucket.filter((e) => COMPLETED_STATUSES.includes(e?.status)).length;
      const overdue = bucket.filter((e) => e?.status === "Overdue").length;

      rows.push({
        reviewerId,
        reviewerName,
        total: bucket.length,
        inProgress,
        overdue,
        completed,
      });
    }

    rows.sort((a, b) => b.total - a.total || a.reviewerName.localeCompare(b.reviewerName));
    return rows;
  }, [reviewerBuckets.assigned, reviewerNameById]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return workloadRows;
    return workloadRows.filter((row) => (row.reviewerName || "").toLowerCase().includes(q));
  }, [workloadRows, search]);

  const selectedReviewer = useMemo(() => {
    if (!selectedReviewerId) return null;

    const reviewerId = String(selectedReviewerId);
    const name = reviewerNameById.get(reviewerId);
    if (!name) return null;

    const bucket = reviewerBuckets.assigned.get(reviewerId) || [];
    const sorted = [...bucket].sort(
      (a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()
    );
    return { reviewerId, name, items: sorted };
  }, [reviewerBuckets.assigned, reviewerNameById, selectedReviewerId]);

  if (loading) {
    return (
      <div className="flex min-h-screen bg-gray-100">
        <ViewerSidebar />
        <main className="flex-1 p-6 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-700 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading reviewer workload...</p>
          </div>
        </main>
      </div>
    );
  }

  const assignedCount = esmps.filter((e) => !!e?.reviewer_id).length;
  const activeCount = esmps.filter((e) => IN_PROGRESS_STATUSES.includes(e?.status)).length;

  return (
    <div className="flex min-h-screen bg-gray-100">
      <ViewerSidebar />

      <main className="flex-1 p-6 space-y-6">
        <div className="flex flex-col gap-4 bg-gradient-to-r from-green-700 to-green-500 text-white p-5 rounded-xl shadow-md">
          <div>
            <h2 className="text-2xl font-bold">Reviewer Workload</h2>
            <p className="text-sm mt-1">Read-only workload distribution across reviewers</p>
          </div>

          <div className="flex flex-col md:flex-row gap-3 md:items-end md:justify-between">
            <div className="w-full md:max-w-md">
              <label className="block text-xs font-semibold text-green-50 mb-1">
                Search reviewer
              </label>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Type a reviewer name..."
                className="w-full px-3 py-2 rounded-lg text-black border focus:ring-2 focus:ring-green-600 outline-none"
              />
            </div>

            <button
              onClick={loadData}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-white text-green-700 rounded-lg font-semibold hover:bg-gray-100"
            >
              <RefreshCcw size={16} />
              Refresh
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {!error && reviewers.length === 0 && (
          <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-lg">
            No reviewers found.
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatCard label="Assigned to reviewers" value={assignedCount} accentClass="border-green-600" />
          <StatCard label="Unassigned" value={reviewerBuckets.unassigned.length} accentClass="border-gray-500" />
          <StatCard label="Active (In progress)" value={activeCount} accentClass="border-yellow-500" />
        </div>

        <div className="bg-white p-6 rounded-xl shadow">
          <div className="flex items-center gap-2 mb-4">
            <Users size={18} className="text-green-700" />
            <h3 className="text-lg font-semibold text-green-700">Workload by Reviewer</h3>
          </div>

          {error ? (
            <p className="text-sm text-gray-500">Unable to load workload.</p>
          ) : filteredRows.length === 0 ? (
            <p className="text-sm text-gray-500">
              {search.trim()
                ? "No reviewers match your search."
                : "No reviewer assignments found (or reviewer names are unavailable)."}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border px-3 py-2 text-left">Reviewer</th>
                    <th className="border px-3 py-2 text-right">Total</th>
                    <th className="border px-3 py-2 text-right">In progress</th>
                    <th className="border px-3 py-2 text-right">Overdue</th>
                    <th className="border px-3 py-2 text-right">Completed</th>
                    <th className="border px-3 py-2 text-center">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((row) => (
                    <tr key={row.reviewerId} className="hover:bg-green-50">
                      <td className="border px-3 py-2 font-medium">{row.reviewerName}</td>
                      <td className="border px-3 py-2 text-right">{row.total}</td>
                      <td className="border px-3 py-2 text-right">{row.inProgress}</td>
                      <td className="border px-3 py-2 text-right">{row.overdue}</td>
                      <td className="border px-3 py-2 text-right">{row.completed}</td>
                      <td className="border px-3 py-2 text-center">
                        <button
                          onClick={() => setSelectedReviewerId(row.reviewerId)}
                          className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {selectedReviewer && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start gap-4 mb-4">
                <div>
                  <h3 className="text-2xl font-bold text-green-700">{selectedReviewer.name}</h3>
                  <p className="text-xs text-gray-500 mt-1">
                    ESMPs assigned: {selectedReviewer.items.length}
                  </p>
                </div>
                <button
                  onClick={() => setSelectedReviewerId(null)}
                  className="text-3xl text-gray-500 hover:text-gray-700"
                  aria-label="Close reviewer details"
                >
                  ×
                </button>
              </div>

              {selectedReviewer.items.length === 0 ? (
                <p className="text-sm text-gray-500">No ESMPs assigned.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-sm">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="border px-3 py-2 text-left">ESMP ID</th>
                        <th className="border px-3 py-2 text-left">Project</th>
                        <th className="border px-3 py-2 text-left">Cycle</th>
                        <th className="border px-3 py-2 text-left">Sector</th>
                        <th className="border px-3 py-2 text-left">Status</th>
                        <th className="border px-3 py-2 text-left">Deadline</th>
                        <th className="border px-3 py-2 text-left">Submitted</th>
                        <th className="border px-3 py-2 text-left">Document</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedReviewer.items.map((item) => (
                        <tr key={item.id} className="hover:bg-green-50">
                          <td className="border px-3 py-2 font-medium text-blue-700">
                            {item.esmp_id || "N/A"}
                          </td>
                          <td className="border px-3 py-2">{item.project_name || "N/A"}</td>
                          <td className="border px-3 py-2">{item.cycle || "—"}</td>
                          <td className="border px-3 py-2">{item.sector || "—"}</td>
                          <td className="border px-3 py-2">{item.status || "Unknown"}</td>
                          <td className="border px-3 py-2">
                            {item.deadline ? new Date(item.deadline).toLocaleDateString() : "—"}
                          </td>
                          <td className="border px-3 py-2">
                            {item.createdAt ? new Date(item.createdAt).toLocaleDateString() : "—"}
                          </td>
                          <td className="border px-3 py-2">
                            {item.file_path ? (
                              <button
                                type="button"
                                onClick={() => downloadEsmpFile(item)}
                                className="text-blue-600 hover:underline"
                              >
                                {item.file_name || "Open document"}
                              </button>
                            ) : (
                              <span className="text-gray-500">No file</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="flex justify-end mt-6">
                <button
                  onClick={() => setSelectedReviewerId(null)}
                  className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
